import useTrack from 'tracks/hooks/useTrack';
import { hideLoading, showLoading } from '@/components/common/Indicator/pageIndicator';
import useCheckSession from '@/hooks/check-session';
import useReferralUrl from '@/hooks/referral-url';
import { isIosSupportShowTabBar } from '@/utils/common/app-event';
import useAppPageResume from '@/hooks/useAppPageResume';
import useAds from '@/hooks/ads';
import { useEffect } from 'react';
import Router from 'next/router';
import callAppFunction from '@/utils/bridge';
import Constants from '@/constants/const';
import { isSayWeeeApp } from '@/utils/common/device';
import { getCookie, deleteCookie, setCookie } from '@/utils/cookie';
import usePageMountEffect from '@/hooks/usePageMountEffect';
import useRemoveQuery from '@/hooks/useRemoveQuery';
import md5 from 'crypto-js/md5';
import { Fingerprint, DEVICE_ID_V2, SIGNATURE_TOKEN } from '@weee-fe/core';

const NO_LOADING_ANIMATION = ['/category', '/order/list', '/account/perks', '/search'];

/**
 * 当用户第一次访问网站时记录下url
 */
export const setUserFirstLandingUrl = () => {
  let landingUrl = getCookie(Constants.LANDING_URL);
  if (!landingUrl) {
    landingUrl = `${window.location.pathname}${window.location.search}`;
    setCookie(
      Constants.LANDING_URL,
      landingUrl,
      {},
      {
        maxAge: Constants.COMMON_EXPIRED_TIME
      }
    );
  }
};

const setUserFirstPageUrlToSessionStorage = () => {
  if (!sessionStorage.getItem(Constants.FIRST_PAGE_URL_IN_APP)) {
    sessionStorage.setItem(Constants.FIRST_PAGE_URL_IN_APP, window.location.pathname);
  }
};

const traceId = md5(`${new Date().getTime()}`).toString();

const GlobalLogicComponent = props => {
  //检查weee_session_token是否过期
  useCheckSession();
  //埋点
  useTrack();
  //给返利联盟用户url添加referral_id
  useReferralUrl();
  //监听app页面resume事件
  useAppPageResume();
  //广告id处理
  useAds();
  //删除 w_source, 避免重复生成相同的 session
  useRemoveQuery(['w_source']);

  useEffect(() => {
    setUserFirstLandingUrl();
    setUserFirstPageUrlToSessionStorage();

    const setAppStatusBarHeight = () => {
      const queryParams = new URLSearchParams(window.location.search);
      const statusBarHeight = queryParams.get('statusBarHeight');
      const fullscreen = queryParams.get('fullscreen');
      const isApp = isSayWeeeApp();
      if (isApp && statusBarHeight && fullscreen === '1') {
        sessionStorage.setItem('weee-app-status-bar-height', statusBarHeight.toString());
      }
    };
    // 设置app状态栏高度给新站全局设置app状态栏高度使用
    setAppStatusBarHeight();

    if (isSayWeeeApp()) {
      callAppFunction('onContentFinish');
    }
  }, []);

  useEffect(() => {
    const show = () => {
      const isLoading = NO_LOADING_ANIMATION.some(path => {
        return Router.pathname.includes(path);
      });
      if (isLoading) {
        return;
      }
      showLoading();
    };
    const hide = () => {
      hideLoading();
    };

    Router.events.on('routeChangeStart', show);
    Router.events.on('routeChangeComplete', hide);
    Router.events.on('routeChangeError', hide);

    return () => {
      hide();
      Router.events.off('routeChangeStart', show);
      Router.events.off('routeChangeComplete', hide);
      Router.events.off('routeChangeError', hide);
    };
  }, []);

  usePageMountEffect(() => {
    const isFirstLoginSuccess = getCookie(Constants.LOGIN_SUCCESS) === '1';
    const isFirstRegisterSuccess = getCookie(Constants.REGISTRATION_SUCCESS) === '1';
    const successMethod = getCookie(Constants.SUCCESS_METHOD);
    if (isFirstLoginSuccess) {
      if (window.pixelLogin) {
        window.pixelLogin?.({
          method: successMethod
        });
      } else {
        window.__weeeLayers = [
          {
            event: 'login_success',
            params: {
              method: successMethod
            }
          }
        ];
      }
    }
    if (isFirstRegisterSuccess) {
      if (window.pixelRegistration) {
        window.pixelRegistration?.({
          method: successMethod
        });
      } else {
        window.__weeeLayers = [
          {
            event: 'registration_success',
            params: {
              method: successMethod
            }
          }
        ];
      }
    }
    //gtm delete
    deleteCookie(Constants.REGISTRATION_SUCCESS);
    deleteCookie(Constants.LOGIN_SUCCESS);
    deleteCookie(Constants.SUCCESS_METHOD);
  }, []);

  useEffect(() => {
    if (traceId) {
      window?.sessionStorage?.setItem('tempTraceId', traceId);
    }
    const fn = async () => {
      try {
        // const { Fingerprint, DEVICE_ID_V2, SIGNATURE_TOKEN } = await import('@weee-fe/core');
        const finger = new Fingerprint({
          domain: process.env.DOMAIN_HOST || process.env.NEXT_PUBLIC_DOMAIN_HOST
        });
        const fingerData = await finger.init();
        if (!isSayWeeeApp() && !fingerData?.[DEVICE_ID_V2]) {
          const visitorId = await finger.createFingerprint();
          let signatureToken = '';
          if (visitorId) {
            signatureToken = finger.createEncrptFingerprint(visitorId);
            setCookie(DEVICE_ID_V2, visitorId);
            setCookie(SIGNATURE_TOKEN, signatureToken);
          }
          setTimeout(() => {
            window.raptor?.reportLogs({
              event: 'fingerprint_init_create',
              data: {
                fingerData: {
                  d2: visitorId,
                  st: signatureToken,
                  traceId: traceId
                },
                ua: window.navigator.userAgent
              }
            });
          }, 3000);
        } else {
          setCookie(DEVICE_ID_V2, fingerData[DEVICE_ID_V2]);
          setCookie(SIGNATURE_TOKEN, fingerData[SIGNATURE_TOKEN]);
        }
        setTimeout(() => {
          window.raptor?.reportLogs({
            event: 'fingerprint_init',
            data: {
              fingerData: fingerData,
              ua: window.navigator.userAgent,
              traceId: traceId
            }
          });
        }, 3000);
      } catch (error) {
        setTimeout(() => {
          window.raptor?.reportLogs({
            event: 'fingerprint_init_error',
            data: {
              error,
              ua: window.navigator.userAgent
            }
          });
        }, 3000);
      }
    };
    fn();
  }, []);
  return null;
};

export default GlobalLogicComponent;
